/**
 * Theme Context
 * จัดการ theme modes: light, dark, kiosk
 * รองรับ glassmorphism toggle
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark' | 'kiosk';

interface ThemeContextType {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleDarkMode: () => void;
  toggleKioskMode: () => void;
  isGlassmorphism: boolean;
  setGlassmorphism: (enabled: boolean) => void;
  isDark: boolean;
  isKiosk: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  // Load theme from localStorage
  const [mode, setMode] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'light';
    const saved = localStorage.getItem('sabeang-theme-mode');
    return (saved as ThemeMode) || 'light';
  });
  
  // Load glassmorphism preference
  const [isGlassmorphism, setGlassmorphism] = useState(() => {
    if (typeof window === 'undefined') return true;
    const saved = localStorage.getItem('sabeang-glassmorphism');
    return saved !== 'false'; // Default to true
  });

  // Apply theme to document
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Save to localStorage
    localStorage.setItem('sabeang-theme-mode', mode);
    
    // Set data attribute for CSS
    document.documentElement.setAttribute('data-theme', mode);
    
    // Apply theme class
    document.documentElement.classList.remove('light', 'dark', 'kiosk');
    document.documentElement.classList.add(mode);
    
    console.log('[ThemeContext] Theme changed to:', mode);
  }, [mode]);

  // Apply glassmorphism preference
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    localStorage.setItem('sabeang-glassmorphism', String(isGlassmorphism));
    
    if (isGlassmorphism) {
      document.documentElement.classList.add('glassmorphism');
    } else {
      document.documentElement.classList.remove('glassmorphism');
    }
    
    console.log('[ThemeContext] Glassmorphism:', isGlassmorphism);
  }, [isGlassmorphism]);

  // Toggle between light and dark
  const toggleDarkMode = () => {
    setMode(prev => prev === 'dark' ? 'light' : 'dark');
  };

  // Toggle kiosk mode
  const toggleKioskMode = () => {
    setMode(prev => prev === 'kiosk' ? 'light' : 'kiosk');
  };

  const value: ThemeContextType = {
    mode,
    setMode,
    toggleDarkMode,
    toggleKioskMode,
    isGlassmorphism,
    setGlassmorphism,
    isDark: mode === 'dark',
    isKiosk: mode === 'kiosk',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
